import { ChildProps } from '@apollo/client/react/hoc';
import { MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';

import { Location } from 'history';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCButton, buttonClassName, RCAlertBox, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { Modal, SanitizedRender } from '@reverbdotcom/commons';
import { CheckCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import URLHelpers from '../../shared/url_helpers';
import { useUpdateGearCollectionItemMutation } from '../../gear_collections/shared/form/helpers/operations';
import {
  SearchPriceGuideToolGearCollectionItems,
  CanonicalProduct,
  core_apimessages_GearCollectionItemEntry,
  core_apimessages_GearCollectionItem_SeedType,
  core_apimessages_GearCollectionItemEntry_Source,
  CSP,
  Core_Crupdate_GearCollectionItemMutation,
  Core_Crupdate_GearCollectionItemMutationVariables,
} from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { connect } from '@reverbdotcom/commons/src/connect';

export const COMPONENT_NAME = 'PriceGuideToolGearCollectionButton';
const GEAR_COLLECTION_SEED_TYPE_FOR_PRICE_GUIDE_TOOL = 'CanonicalProduct';
const GEAR_COLLECTION_ITEM_SOURCE_FOR_PRICE_GUIDE_TOOL = 'PRICE_GUIDE_TOOL';

export const GEAR_COLLECTION_CONDITION_QUERY_PARAM_KEY = 'conditionSlug';
export const GEAR_COLLECTION_VARIANT_QUERY_PARAM_KEY = 'variantId';
export const GEAR_COLLECTION_QUERY_PARAM_KEYS_FOR_REDIRECTS = [
  GEAR_COLLECTION_CONDITION_QUERY_PARAM_KEY,
  GEAR_COLLECTION_VARIANT_QUERY_PARAM_KEY,
];

interface IExternalProps {
  csp: CSP;
  conditionUuid: string;
  canonicalProduct: CanonicalProduct;
  location?: Location;
}

type IProps = ChildProps<IExternalProps, SearchPriceGuideToolGearCollectionItems.Query>;

export function buildGearCollectionItemEntry({
  brand,
  categoryRootUuid,
  conditionSlug,
  subcategoryUuids,
  canonicalProduct,
}) {
  const variantAttributes = {
    canonicalProductId: canonicalProduct.id,
    seedId: canonicalProduct.id,
    seedType: core_apimessages_GearCollectionItem_SeedType[GEAR_COLLECTION_SEED_TYPE_FOR_PRICE_GUIDE_TOOL],
    title: `${brand.name} ${canonicalProduct.name}`,
    model: canonicalProduct.model,
    finish: canonicalProduct.finish,
    year: canonicalProduct.year,
  };

  return {
    categoryRootUuid,
    categoryUuids: subcategoryUuids,
    conditionSlug,
    make: brand.name,
    source: core_apimessages_GearCollectionItemEntry_Source[GEAR_COLLECTION_ITEM_SOURCE_FOR_PRICE_GUIDE_TOOL],
    ...variantAttributes,
  };
}

interface ICreateGearCollectionItemProps {
  csp: CSP;
  gearCollectionItemEntry: core_apimessages_GearCollectionItemEntry;
  submitMutation: MutationFunction<Core_Crupdate_GearCollectionItemMutation, Core_Crupdate_GearCollectionItemMutationVariables>;
  setNewlyCreatedGearCollectionItemId: React.Dispatch<React.SetStateAction<string>>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  setHasGearCollectionItemCreationError: React.Dispatch<React.SetStateAction<boolean>>;
}

export function createGearCollectionItem({
  csp,
  gearCollectionItemEntry,
  submitMutation,
  setNewlyCreatedGearCollectionItemId,
  setIsSubmitting,
  setHasGearCollectionItemCreationError,
}: ICreateGearCollectionItemProps) {
  setIsSubmitting(true);

  const variables: Core_Crupdate_GearCollectionItemMutationVariables = {
    input: {
      gearCollectionItemEntry,
    },
  };

  trackEvent({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedAddToGearCollectionFromPriceGuideTool,
    cspId: csp.id,
    cspSlug: csp.slug,
    cpId: gearCollectionItemEntry.canonicalProductId,
    conditionSlug: gearCollectionItemEntry.conditionSlug,
  });

  submitMutation({
    variables,
    errorPolicy: 'all',
  }).then((response) => {
    const { data, errors = [] } = response;
    const hasErrors = !!errors.length;

    if (hasErrors) {
      setIsSubmitting(false);
      setHasGearCollectionItemCreationError(true);
    } else {
      setNewlyCreatedGearCollectionItemId(data.crupdateGearCollectionItem.id);
      setIsSubmitting(false);
    }
  }).catch(() => {
    setIsSubmitting(false);
    setHasGearCollectionItemCreationError(true);
  });
}

interface IGearCollectionItemErroModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

export function GearCollectionItemErrorModal({
  isOpen,
  handleClose,
}: IGearCollectionItemErroModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      headerTitle={I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.errors.title')}
      onRequestClose={handleClose}
      paddedBody
      size="wide"
    >
      <RCAlertBox type="error">
        <SanitizedRender
          html={I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.errors.message', {
            tagOpen: `<a href="${URLHelpers.contactUsPath}" target="_blank">`,
            tagClose: '</a>',
          })}
        />
      </RCAlertBox>
    </Modal>
  );
}

export function PriceGuideToolGearCollectionButton({
  conditionUuid,
  csp,
  data,
  location,
  canonicalProduct,
}: IProps) {
  const user = useUser();
  const [submitMutation] = useUpdateGearCollectionItemMutation();

  const {
    brand,
    categoryRootUuid,
    categoryUuids: subcategoryUuids,
  } = csp;

  const {
    loading,
    gearCollectionItemsSearch = {},
  } = data;
  const { gearCollectionItems = [] } = gearCollectionItemsSearch;
  const hasGearCollectionItem = !!gearCollectionItems.length;

  const [newlyCreatedGearCollectionItemId, setNewlyCreatedGearCollectionItemId] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasGearCollectionItemCreationError, setHasGearCollectionItemCreationError] = React.useState(false);
  const [showSignInModal, setShowSignInModal] = React.useState(false);

  const hasNewlyCreatedGearCollectionItem = !!newlyCreatedGearCollectionItemId.length;
  const gearCollectionItemId = hasGearCollectionItem ? gearCollectionItems[0].id : newlyCreatedGearCollectionItemId;
  const conditionSlug = conditionSlugForUuid(conditionUuid);

  const locationQuery = location?.query || {};
  const redirectQueryParamsForSignIn = {
    ...locationQuery,
    ...{
      [GEAR_COLLECTION_CONDITION_QUERY_PARAM_KEY]: conditionSlug,
      [GEAR_COLLECTION_VARIANT_QUERY_PARAM_KEY]: canonicalProduct.id,
    },
  };
  const redirectToPathForSignIn = URLHelpers.priceGuideShowPath(csp.slug, redirectQueryParamsForSignIn);

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: COMPONENT_NAME,
    cspId: csp.id,
    cspSlug: csp.slug,
    cpId: canonicalProduct.id,
    conditionSlug,
    hasCanonicalProductInGearCollection: hasGearCollectionItem,
  }, !loading);

  if (loading) { return null; }

  return (
    <div className="mt-4 width-100">
      <SignupSigninModal
        message={I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.loginMessage')}
        active={showSignInModal}
        trackSource="PriceGuideTool"
        redirectTo={redirectToPathForSignIn}
        onRequestClose={() => setShowSignInModal(false)}
      />
      <GearCollectionItemErrorModal
        isOpen={hasGearCollectionItemCreationError}
        handleClose={() => setHasGearCollectionItemCreationError(false)}
      />
      {hasGearCollectionItem || hasNewlyCreatedGearCollectionItem ?
        <CoreLink
          to={URLHelpers.gearCollectionItemPath(gearCollectionItemId)}
          className={`${buttonClassName({ variant: 'muted', fullWidth: true })} mt-2`}
          target="_blank"
        >
          <RCTextWithIcon
            placement="right"
            svgComponent={CheckCircleIcon}
          >
            {I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.viewCollectionButtonText')}
          </RCTextWithIcon>
        </CoreLink>
        :
        <>
          <div className="size-70 weight-normal mb-2 mobile-d-none">
            {I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.ownOne')}
          </div>
          <RCButton
            variant="muted"
            isSubmit
            disabled={isSubmitting}
            onClick={() => {
              if (user.loggedOut) {
                setShowSignInModal(true);
              } else {
                createGearCollectionItem({
                  csp: csp,
                  gearCollectionItemEntry: buildGearCollectionItemEntry({
                    brand,
                    categoryRootUuid,
                    conditionSlug,
                    subcategoryUuids,
                    canonicalProduct,
                  }),
                  submitMutation,
                  setNewlyCreatedGearCollectionItemId,
                  setIsSubmitting,
                  setHasGearCollectionItemCreationError,
                });
              }
            }}
            fullWidth
          >
            {I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.addToCollectionButtonText')}
          </RCButton>
          <div className="size-70 weight-normal mt-2 mobile-d-none">
            {I18n.t('discovery.priceGuideTool.show.form.gearCollectionButton.addingGear')}
          </div>
        </>
      }
    </div>
  );
}

const withQuery = withGraphql<IProps, SearchPriceGuideToolGearCollectionItems.Query>(
  gql`
  query Search_PriceGuideTool_GearCollectionItems(
    $canonicalProductIds: [String]
  ) {
    gearCollectionItemsSearch(input: {
      as: OWNER,
      searchInput: {
        canonicalProductIds: $canonicalProductIds
      }
    }) {
      gearCollectionItems {
        _id
        id
      }
    }
  }
  `,
  {
    options: ownProps => {
      return {
        fetchPolicy: 'network-only',
        ssr: false,
        variables: {
          canonicalProductIds: [ownProps.canonicalProduct.id],
        },
      };
    },
  },
);

export default connect<IExternalProps>([
  withQuery,
])(PriceGuideToolGearCollectionButton);
